import React, { useMemo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import ContinueButtonBar from '../components/ContinueButtonBar';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import { useWheelLocale } from '../hooks/useWheelLocale';
import CareerHistorySurveyChatComposer from './CareerHistorySurveyChatComposer';
import CareerHistorySurveyChatThread from './CareerHistorySurveyChatThread';
import { useCareerHistorySurveyFlow } from './useCareerHistorySurveyFlow';

export interface CareerHistorySurveyChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenPdfPreview?: () => void;
}

const CareerHistorySurveyChatFlow: React.FC<CareerHistorySurveyChatFlowProps> = ({
  documentTitle,
  onBack,
  currentEmail,
  invitationId,
  onOpenPdfPreview,
}) => {
  const { t } = useLanguage();
  const wheelLocale = useWheelLocale();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useCareerHistorySurveyFlow(currentEmail, invitationId, t, wheelLocale);

  const greeting = useMemo(
    () => interpolateI18n(t('submitDocuments.careerHistorySurvey.greetingPrefix'), { documentTitle }),
    [t, documentTitle]
  );

  const completeThankYou = useMemo(
    () => interpolateI18n(t('submitDocuments.careerHistorySurvey.completeThankYou'), { documentTitle }),
    [t, documentTitle]
  );

  const atComplete = flow.isComplete;
  const { showIntro: showCompleteMessage, showCta: showPreviewPdfCta } = useStaggeredPreviewCta(atComplete);

  useChatThreadScroll(flow.threadEndRef, [
    flow.isLoading,
    flow.step,
    flow.completed.length,
    flow.committed.size,
    flow.recordGateCommitted,
    flow.editIndex,
    flow.pendingGateEntryIndex,
    flow.entryFieldsExpanded,
    flow.sectionWorkHistoryDone,
    flow.sectionPneuDone,
    flow.pneuGateCommitted,
    flow.pneuDraft.gateAnswer,
    atComplete,
    showCompleteMessage,
  ], { focusRef: flow.currentQuestionRef });

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            <AssistantBotRow animateIn>{greeting}</AssistantBotRow>
            {flow.isLoading ? (
              <AssistantBotRow animateIn>{t('submitDocuments.careerHistorySurvey.loading')}</AssistantBotRow>
            ) : (
              <CareerHistorySurveyChatThread flow={flow} />
            )}
            {atComplete && showCompleteMessage && (
              <AssistantBotRow animateIn>{completeThankYou}</AssistantBotRow>
            )}
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.reviewMode && flow.editIndex !== null && flow.showFieldEdit ? (
              <ContinueButtonBar
                onContinue={flow.collapseEditReview}
                label={t('submitDocuments.careerHistorySurvey.backToReview')}
              />
            ) : null}
            {atComplete && showPreviewPdfCta && onOpenPdfPreview ? (
              <PdfPreviewActionButton
                label={t('submitDocuments.careerHistorySurvey.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            ) : null}
            {!flow.isLoading && !flow.isComplete ? (
              <CareerHistorySurveyChatComposer flow={flow} wheelLocale={wheelLocale} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default CareerHistorySurveyChatFlow;
